import {
  ProgressCheckpoint,
  type CheckpointState,
  type LabelAlignment,
} from "./ProgressCheckpoint";
import { cn } from "@/lib/utils";

export type AnalysisStatus =
  | "collecting_data"
  | "validating"
  | "analyzing"
  | "analyzed"
  | "processing"
  | "completed"
  | "failed";

interface CheckpointConfig {
  id: string;
  label: string;
  index: number;
  progressPercentage: number;
}

const CHECKPOINTS: CheckpointConfig[] = [
  {
    id: "collecting_data",
    label: "Data Collection",
    index: 0,
    progressPercentage: 0,
  },
  {
    id: "validating",
    label: "Validation",
    index: 1,
    progressPercentage: 25,
  },
  {
    id: "analyzing",
    label: "Analysis",
    index: 2,
    progressPercentage: 50,
  },
  {
    id: "analyzed",
    label: "Ingestion",
    index: 3,
    progressPercentage: 75,
  },
  {
    id: "completed",
    label: "Complete",
    index: 4,
    progressPercentage: 100,
  },
];

interface AnalysisProgressBarProps {
  status: AnalysisStatus;
  failedAtStage?: string | null;
  showLabels?: boolean;
}

function getCheckpointState(
  checkpointIndex: number,
  currentStatus: AnalysisStatus,
  failedAtStage?: string | null,
): CheckpointState {
  // Failed state: show progress up to failure point
  if (currentStatus === "failed") {
    const failedCheckpoint = CHECKPOINTS.find((cp) => cp.id === failedAtStage);
    if (failedCheckpoint) {
      const failedIndex = failedCheckpoint.index;
      if (checkpointIndex < failedIndex) {
        return "completed";
      } else if (checkpointIndex === failedIndex) {
        return "failed";
      } else {
        return "pending";
      }
    }
    return "pending";
  }

  const currentCheckpoint = CHECKPOINTS.find(
    (cp) =>
      cp.id === currentStatus ||
      (currentStatus === "processing" && cp.id === "completed"),
  );

  if (!currentCheckpoint) return "pending";

  const currentIndex = currentCheckpoint.index;

  // Determine state based on checkpoint position
  if (checkpointIndex < currentIndex) {
    return "completed";
  } else if (checkpointIndex === currentIndex) {
    return currentStatus === "completed" ? "completed" : "active";
  } else {
    return "pending";
  }
}

function getProgressPercentage(
  status: AnalysisStatus,
  failedAtStage?: string | null,
): number {
  if (status === "failed") {
    const checkpoint = CHECKPOINTS.find((cp) => cp.id === failedAtStage);
    return checkpoint?.progressPercentage ?? 0;
  }

  const checkpoint = CHECKPOINTS.find(
    (cp) =>
      cp.id === status || (status === "processing" && cp.id === "completed"),
  );
  return checkpoint?.progressPercentage ?? 0;
}

export function AnalysisProgressBar({
  status,
  failedAtStage,
  showLabels = false,
}: AnalysisProgressBarProps) {
  const progressPercentage = getProgressPercentage(status, failedAtStage);

  const currentLabel =
    status === "failed"
      ? `Failed at ${CHECKPOINTS.find((cp) => cp.id === failedAtStage)?.label || "Unknown"}`
      : CHECKPOINTS.find(
          (cp) =>
            cp.id === status || (status === "processing" && cp.id === "completed"),
        )?.label || "Processing...";

  return (
    <div className="w-full">
      {/* Current Stage Label */}
      <div className="mb-2">
        <p className="text-xs font-medium text-muted-foreground">
          {currentLabel}
        </p>
      </div>

      {/* Progress Bar Container */}
      <div className={cn("relative pt-2", showLabels ? "pb-8" : "pb-2")}>
        {/* Background Track */}
        <div className="absolute top-5 left-0 right-0 h-2 bg-muted rounded-full" />

        {/* Progress Fill */}
        <div
          className={cn(
            "absolute top-5 left-0 h-2 rounded-full transition-all duration-700 ease-out overflow-hidden bg-primary",
          )}
          style={{ width: `${progressPercentage}%` }}
        >
          {status !== "completed" && status !== "failed" && (
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "linear-gradient(45deg, rgba(255, 255, 255, 0.2) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0.2) 75%, transparent 75%, transparent)",
                backgroundSize: "30px 30px",
                animation: "shimmer 2s linear infinite",
              }}
            />
          )}
        </div>

        {/* Checkpoints */}
        <div className="relative flex justify-between">
          {CHECKPOINTS.map((checkpoint, index) => {
            const state = getCheckpointState(index, status, failedAtStage);
            const labelAlignment: LabelAlignment =
              index === 0
                ? "start"
                : index === CHECKPOINTS.length - 1
                  ? "end"
                  : "center";

            return (
              <div key={checkpoint.id} className="flex-shrink-0">
                <ProgressCheckpoint
                  state={state}
                  label={checkpoint.label}
                  showLabel={showLabels}
                  labelAlignment={labelAlignment}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
